import { useEffect, useState } from 'react'
import { api } from '@/api/client'
import { fmtDateTime } from '@/lib/format'

export type StatusChange = { status: string; at: string; blocker?: string }

export type Progress = {
  workflow_id: string; job_id?: string; status: string; blocker?: string
  created_at?: string; updated_at?: string
  status_history?: StatusChange[]
  import?: { status: string; error?: string }
  /** The builder's open question for the expert who submitted the template. */
  question?: { message: string; requested_at: string; revision: number }
  can_answer?: boolean
}

/** What the build is doing, in the expert's words. A developer runs and unblocks it. */
const BUILD_STATE: Record<string, { label: string; tone: string; detail: string }> = {
  not_started: { label: 'Preparing', tone: 'wait', detail: 'The approved workflow is being prepared for its build.' },
  pending_delivery: { label: 'Preparing', tone: 'wait', detail: 'The build request is on its way to the authoring service.' },
  queued: { label: 'Queued', tone: 'wait', detail: 'The build is waiting for a free agent.' },
  capacity_wait: { label: 'Queued', tone: 'wait', detail: 'The build is waiting for agent capacity.' },
  running: { label: 'Building', tone: 'live', detail: 'An agent is implementing the tasks and their graders.' },
  blocked_expert: { label: 'Waiting for an answer', tone: 'warn', detail: 'The agent asked a question about this workflow.' },
  blocked_dev: { label: 'With a developer', tone: 'warn', detail: 'The build hit a technical problem a developer has to resolve.' },
  blocked: { label: 'With a developer', tone: 'warn', detail: 'The build hit a technical problem a developer has to resolve.' },
  succeeded: { label: 'Build complete', tone: 'ok', detail: 'The tasks are built and graded. The quality check opens on its own.' },
  failed: { label: 'Build failed', tone: 'err', detail: 'A developer will look at the failure and restart the build.' },
  cancelled: { label: 'Build stopped', tone: 'wait', detail: 'A developer stopped this build.' },
  rejected: { label: 'Build refused', tone: 'err', detail: 'The authoring service refused the build request.' },
  cancellation_requested: { label: 'Stopping', tone: 'wait', detail: 'A developer is stopping this build.' },
  unavailable: { label: 'Status unavailable', tone: 'warn', detail: 'The build status cannot be read right now. It is retried automatically.' },
}
const IMPORT_STATE: Record<string, string> = {
  queued: 'The built tasks are queued for import.', running: 'The built tasks are being imported.',
  completed: 'The built tasks are imported. The quality check opens on its own.',
  partial: 'Some built tasks could not be imported. A developer is following up.',
  failed: 'The import failed. A developer is following up.',
}
const LIVE = ['not_started', 'pending_delivery', 'queued', 'capacity_wait', 'running', 'cancellation_requested', 'unavailable']

/** Build progress for the expert who submitted the template, the reviewer who approved its
 * workflows and developers. When the agent asks a question, the template's expert answers it here
 * and the build continues with the answer; everything else about the build is read-only.
 */
export function WorkflowProgress({ workflowId, revision, onChanged }: { workflowId: string; revision: number; onChanged: () => void }) {
  const [value, setValue] = useState<Progress | null>(null)
  const [error, setError] = useState('')
  const [answer, setAnswer] = useState('')
  const [busy, setBusy] = useState(false)
  function send() {
    setBusy(true); setError('')
    void api.post(`/scientific/workflows/${workflowId}/answer`, { revision: value?.question?.revision, answer: answer.trim() })
      .then(() => { setAnswer(''); onChanged() }).catch(e => setError((e as Error).message)).finally(() => setBusy(false))
  }
  useEffect(() => {
    let disposed = false
    const read = () => api.get<Progress>(`/scientific/workflows/${workflowId}/progress`)
      .then(result => { if (!disposed) { setValue(result); setError('') } })
      .catch(e => { if (!disposed) setError((e as Error).message) })
    void read()
    // A build that is blocked can be resumed by a developer and start running again.
    const timer = window.setInterval(read, 15000)
    return () => { disposed = true; window.clearInterval(timer) }
  }, [workflowId, revision])
  if (error && !value) return <p className="note err" role="alert">{error}</p>
  if (!value) return <p className="small muted" role="status">Loading build progress…</p>
  const state = BUILD_STATE[value.status] ?? { label: value.status.replaceAll('_', ' '), tone: 'wait', detail: '' }
  const since = value.updated_at ?? value.created_at
  const history = value.status_history ?? []
  return <div className="science-build-progress">
    <p className="science-build-state">
      <span className={`science-build-dot ${state.tone}${LIVE.includes(value.status) ? ' is-live' : ''}`} aria-hidden="true" />
      <span role="status"><strong>{state.label}</strong>{since && <> · last update {fmtDateTime(since)}</>}</span>
    </p>
    {state.detail && <p className="small muted">{state.detail}</p>}
    {value.question ? <section className="science-build-question" aria-label="Question from the agent">
      <p className="small muted">The agent asked on {fmtDateTime(value.question.requested_at)}:</p>
      <blockquote className="prewrap">{value.question.message}</blockquote>
      {value.can_answer && <form onSubmit={e => { e.preventDefault(); send() }}>
        <label className="field"><span>Your answer</span>
          <textarea value={answer} maxLength={4000} disabled={busy} onChange={e => setAnswer(e.target.value)} /></label>
        {error && <p className="note err" role="alert">{error}</p>}
        <button className="primary" disabled={busy || !answer.trim()}>Send answer and continue the build</button>
      </form>}
    </section> : value.blocker && <p className="small science-build-blocker">{value.blocker}</p>}
    {history.length > 1 && <details className="science-build-history">
      <summary className="small">Build history</summary>
      {/* Oldest first: a build that stalled and was resumed reads differently from one that never did. */}
      <ol>{history.map((entry, index) => {
        const past = BUILD_STATE[entry.status]
        return <li key={`${entry.at}-${index}`}>
          <span className={`science-build-dot ${past?.tone ?? 'wait'}`} aria-hidden="true" />
          <span className="small">
            <strong>{past?.label ?? entry.status.replaceAll('_', ' ')}</strong>
            {entry.at && <span className="muted"> · {fmtDateTime(entry.at)}</span>}
            {entry.blocker && <span className="science-build-history-note">{entry.blocker}</span>}
          </span>
        </li>
      })}</ol>
    </details>}
    {value.import && <p className="small muted">{IMPORT_STATE[value.import.status] ?? `Import: ${value.import.status}`}
      {value.import.error && <> {value.import.error}</>}</p>}
  </div>
}
